import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, message } from 'antd';
import { useMutation } from '@apollo/client';

import { ProTable, Link, DateDisplay } from 'components/layout';
import { formatEnum, stringSorter } from 'utils';
import { EditButton, DeleteButton } from 'components/button';
import { useHasScopeTypeInProject, useLoading } from 'hooks';
import {
  DELETE_SCHEDULED_REPORT_SUBMISSION,
  GET_REPORT_SUBMISSION,
} from 'gql/reportSubmissions';
import { SCOPE_TYPES } from 'constants/scopes';
import useProjectFrozen from 'hooks/useProjectFrozen';

const reportSubmissionUrl = ({ projectId, id }) =>
  `/projects/${projectId}/report-submissions/${id}`;

const ReportSubmissionsTable = ({ reportSubmissions, ...props }) => {
  const { projectId } = useParams();
  const projectFrozen = useProjectFrozen(projectId);
  const [deleteSchedule, { loading }] = useMutation(
    DELETE_SCHEDULED_REPORT_SUBMISSION,
    {
      refetchQueries: [
        {
          query: GET_REPORT_SUBMISSION,
          variables: {
            projectIds: [projectId],
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        message.success('Report Submission Schedule deleted successfully');
      },
      onError: () => {},
    }
  );
  const { hasScopeType: canEdit } = useHasScopeTypeInProject(
    SCOPE_TYPES.WRITE_SCHEDULED_REPORT_SUBMISSION
  );
  const { hasScopeType: canSubmit } = useHasScopeTypeInProject(
    SCOPE_TYPES.SUBMIT_REPORT,
    SCOPE_TYPES.SUBMIT_REPORT_WITH_CUSTOM_DATE
  );
  useLoading(loading);

  const columns = [
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      sorter: (a, b) => stringSorter(a.reportType, b.reportType),
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>
          {formatEnum(_)}
        </Link>
      ),
    },
    {
      title: 'Reporting Period',
      dataIndex: 'reportingPeriod',
      sorter: (a, b) => stringSorter(a.reportingPeriod, b.reportingPeriod),
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>{_}</Link>
      ),
    },
    {
      title: 'Scheduled Submission Date',
      dataIndex: 'scheduledSubmissionDate',
      sorter: (a, b) => a.scheduledSubmissionDate - b.scheduledSubmissionDate,
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>
          <DateDisplay date={item.scheduledSubmissionDate} />
        </Link>
      ),
    },
    {
      title: 'Actual Submission Date',
      dataIndex: 'actualSubmissionDate',
      sorter: (a, b) => a.actualSubmissionDate - b.actualSubmissionDate,
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>
          <DateDisplay date={item.actualSubmissionDate} />
        </Link>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      ellipsis: true,
      sorter: (a, b) => stringSorter(a.remarks, b.remarks),
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>{_}</Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      valueEnum: {
        SCHEDULED: 'Scheduled',
        REVIEWING: 'Reviewing',
        COMPLETED: 'Completed',
      },
      sorter: (a, b) => stringSorter(a.status, b.status),
      filters: true,
      onFilter: true,
      render: (_, item) => (
        <Link to={reportSubmissionUrl({ projectId, id: item.id })}>{_}</Link>
      ),
    },
    {
      title: 'Submit',
      hideInTable: !canSubmit || projectFrozen,
      render: (_, item) =>
        item.status === 'SCHEDULED' ? (
          <Link to={reportSubmissionUrl({ projectId, id: item.id })}>
            <Button type="primary">Submit</Button>
          </Link>
        ) : null,
    },
    {
      title: 'Actions',
      width: '250px',
      hideInTable: !canEdit || projectFrozen,
      render: (_, item) => (
        <>
          <Link
            to={`/projects/${projectId}/report-submissions/${item.id}/edit`}
          >
            <EditButton />
          </Link>
          <DeleteButton
            onOk={() => onDelete(item.id)}
            content="Do you want to delete this report submission schedule?"
          />
        </>
      ),
    },
  ];

  const onDelete = (id) => {
    deleteSchedule({
      variables: {
        input: {
          id,
          projectId,
        },
      },
    });
  };

  return (
    <ProTable
      columns={columns}
      dataSource={reportSubmissions}
      pagination={false}
      search={false}
      toolBarRender={false}
      rowKey={({ id }) => id}
      {...props}
    />
  );
};

ReportSubmissionsTable.defaultProps = {
  reportSubmissions: [],
};

ReportSubmissionsTable.propTypes = {
  reportSubmissions: PropTypes.array.isRequired,
};

export default ReportSubmissionsTable;
